import { useState, useEffect, useCallback } from 'react';
import { BadgeCheck, Search, Plus, RefreshCw, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { taxApi } from '@/api/tax.api';
import type { TaxRegistration, TaxJurisdiction } from '@/api/tax.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { Combobox } from '@/components/ui/Combobox';
import { cn } from '@/utils/cn';
import { PartyLookup } from '@/components/lookups/EntityLookups';

const ENTITY_TYPES = ['Company', 'Customer', 'Supplier'];
const REG_TYPES = ['VAT', 'GST', 'Withholding', 'ServiceTax', 'ExciseTax', 'Other'];
const VALIDATION_STATUSES = ['Pending', 'Valid', 'Invalid', 'Expired'];

const STATUS_COLORS: Record<string, string> = {
  Valid:   'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Invalid: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Expired: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  Pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
};

// ── Dialog ────────────────────────────────────────────────────────────────────

interface RegDialogProps {
  open: boolean;
  record: TaxRegistration | null;
  jurisdictions: TaxJurisdiction[];
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_REG: Partial<TaxRegistration> = {
  entityType: 'Company', entityId: '', jurisdictionId: '',
  registrationNumber: '', registrationType: 'VAT',
  effectiveFrom: new Date().toISOString().slice(0, 10), effectiveTo: null,
  validationStatus: 'Pending', isExempt: false, exemptionReason: null,
  isActive: true, remarks: null,
};

const RegDialog = ({ open, record, jurisdictions, onClose, onSaved }: RegDialogProps) => {
  const [form, setForm] = useState<Partial<TaxRegistration>>(EMPTY_REG);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setForm(record ? { ...record } : { ...EMPTY_REG }); setError(null); }, [record, open]);

  if (!open) return null;

  const set = (k: keyof TaxRegistration, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.entityId?.trim() || !form.jurisdictionId || !form.registrationNumber?.trim() || !form.registrationType || !form.effectiveFrom) {
      setError('Entity ID, Jurisdiction, Registration Number, Type and Effective From are required.'); return;
    }
    setSaving(true); setError(null);
    try {
      if (record) { await taxApi.updateRegistration(record.id, form); }
      else { await taxApi.createRegistration(form); }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-9 px-3 rounded-md border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500';
  const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">{record ? 'Edit Registration' : 'New Registration'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          {error && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Entity Type *</label>
              <Combobox value={form.entityType ?? 'Company'} onChange={v => set('entityType', v)} clearable={false}
                options={ENTITY_TYPES.map(t => ({ value: t, label: t }))} />
            </div>
            <div>
              <label className={labelCls}>Registration Type *</label>
              <Combobox value={form.registrationType ?? 'VAT'} onChange={v => set('registrationType', v)} clearable={false}
                options={REG_TYPES.map(t => ({ value: t, label: t }))} />
            </div>
          </div>
          <div>
            {/* Follows Entity Type above: a registration belongs to a company, a customer or a
                supplier, and picking from the wrong register gives an id that looks valid and
                points at nothing. */}
            <PartyLookup
              label={`${form.entityType ?? 'Company'} *`}
              entityType={form.entityType ?? 'Company'}
              value={form.entityId}
              onChange={v => set('entityId', v ?? '')}
            />
          </div>
          <div>
            <label className={labelCls}>Jurisdiction *</label>
            <Combobox value={form.jurisdictionId ?? ''} onChange={v => set('jurisdictionId', v)}
              placeholder="Select…"
              options={jurisdictions.map(j => ({ value: j.id, label: `${j.jurisdictionCode} — ${j.jurisdictionName}` }))} />
          </div>
          <div>
            <label className={labelCls}>Registration Number *</label>
            <input className={inputCls} value={form.registrationNumber ?? ''} onChange={e => set('registrationNumber', e.target.value)} maxLength={100} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Effective From *</label>
              <input type="date" className={inputCls} value={form.effectiveFrom ?? ''} onChange={e => set('effectiveFrom', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Effective To</label>
              <input type="date" className={inputCls} value={form.effectiveTo ?? ''} onChange={e => set('effectiveTo', e.target.value || null)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Validation Status *</label>
            <Combobox value={form.validationStatus ?? 'Pending'} onChange={v => set('validationStatus', v)} clearable={false}
              options={VALIDATION_STATUSES.map(v => ({ value: v, label: v }))} />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={!!form.isExempt} onChange={e => set('isExempt', e.target.checked)} /> Exempt
            </label>
            {record && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={!!form.isActive} onChange={e => set('isActive', e.target.checked)} /> Active
              </label>
            )}
          </div>
          {form.isExempt && (
            <div>
              <label className={labelCls}>Exemption Reason</label>
              <input className={inputCls} value={form.exemptionReason ?? ''} onChange={e => set('exemptionReason', e.target.value || null)} maxLength={300} />
            </div>
          )}
          <div>
            <label className={labelCls}>Remarks</label>
            <textarea className="w-full px-3 py-2 rounded-md border text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
              rows={2} value={form.remarks ?? ''} onChange={e => set('remarks', e.target.value || null)} maxLength={500} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="h-8 px-4 text-xs rounded-md bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
            {saving && <Spinner size="sm" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const TaxRegistrationsPage = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.TAX_REGISTRATION_CREATE as string);
  const canEdit   = hasPermission(Permissions.TAX_REGISTRATION_EDIT as string);
  const canDelete = hasPermission(Permissions.TAX_REGISTRATION_DELETE as string);

  const [items, setItems]   = useState<TaxRegistration[]>([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]     = useState(1);
  const pageSize = 20;

  const [search, setSearch]               = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [dialogOpen, setDialogOpen]       = useState(false);
  const [editRecord, setEditRecord]       = useState<TaxRegistration | null>(null);
  const [deleteTarget, setDeleteTarget]   = useState<TaxRegistration | null>(null);
  const [deleting, setDeleting]           = useState(false);
  const [jurisdictions, setJurisdictions] = useState<TaxJurisdiction[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taxApi.getRegistrations({
        search: search || undefined,
        entityType: entityTypeFilter || undefined,
        validationStatus: statusFilter || undefined,
        pageNumber: page, pageSize,
      });
      const d = res.data?.data;
      setItems(d?.items ?? []);
      setTotal(d?.totalCount ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [search, entityTypeFilter, statusFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    taxApi.getJurisdictions({ pageSize: 500 }).then(r => setJurisdictions(r.data?.data?.items ?? [])).catch(() => {});
  }, []);

  const jurisdictionName = (id: string) => jurisdictions.find(j => j.id === id)?.jurisdictionName ?? id;

  const handleSaved = () => { setDialogOpen(false); setEditRecord(null); load(); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await taxApi.deleteRegistration(deleteTarget.id); setDeleteTarget(null); load(); }
    catch { /* silent */ }
    finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / pageSize);
  const maskId = (id: string) => id.length > 8 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <BadgeCheck size={20} className="text-brand-600" /> Tax Registrations
            </h1>
            <p className="text-sm text-slate-500 dark:text-gray-400 mt-0.5">{total.toLocaleString()} records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 dark:text-gray-400"><RefreshCw size={16} /></button>
            {canCreate && (
              <button onClick={() => { setEditRecord(null); setDialogOpen(true); }}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg">
                <Plus size={15} /> New Registration
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <div className="relative max-w-xs flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search registration number…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
          <Combobox className="w-44" clearable={false}
            value={entityTypeFilter} onChange={v => { setEntityTypeFilter(v); setPage(1); }}
            options={[{ value: '', label: 'All Entity Types' }, ...ENTITY_TYPES.map(t => ({ value: t, label: t }))]} />
          <Combobox className="w-40" clearable={false}
            value={statusFilter} onChange={v => { setStatusFilter(v); setPage(1); }}
            options={[{ value: '', label: 'All Statuses' }, ...VALIDATION_STATUSES.map(v => ({ value: v, label: v }))]} />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <BadgeCheck size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No tax registrations found</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                {['Entity Type', 'Entity ID', 'Jurisdiction', 'Reg. Number', 'Reg. Type', 'Valid. Status', 'Exempt', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      row.entityType === 'Company' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                      row.entityType === 'Customer' ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400')}>
                      {row.entityType}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-slate-500 dark:text-slate-400" title={row.entityId}>{maskId(row.entityId)}</td>
                  <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300">{jurisdictionName(row.jurisdictionId)}</td>
                  <td className="px-3 py-2.5 font-mono font-medium text-slate-800 dark:text-slate-200">{row.registrationNumber}</td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400">{row.registrationType}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold', STATUS_COLORS[row.validationStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                      {row.validationStatus}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {row.isExempt && <span className="px-2 py-0.5 rounded-full text-[10px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">Exempt</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      row.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      {canEdit && <button onClick={() => { setEditRecord(row); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-500"><Edit2 size={13} /></button>}
                      {canDelete && <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronLeft size={16} /></button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      <RegDialog open={dialogOpen} record={editRecord} jurisdictions={jurisdictions}
        onClose={() => { setDialogOpen(false); setEditRecord(null); }} onSaved={handleSaved} />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-bold mb-2">Delete Registration?</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Delete registration <strong>{deleteTarget.registrationNumber}</strong>? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="h-8 px-4 text-xs rounded-md bg-red-600 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
                {deleting && <Spinner size="sm" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
